<template>
  <div>
    <!-- Loans Title and Filters -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-header class="bg-primary text-white pt-50 pb-50">
        <h4 class="mb-0 text-white">
          Loans
        </h4>
      </b-card-header>

      <!-- Error Alert -->
      <b-alert
        v-if="loadingError"
        variant="danger"
        show
        dismissible
        class="m-1 mb-0"
        @dismissed="loadingError = null"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          <span>{{ loadingError }}</span>
        </div>
      </b-alert>

      <!-- Filters Section -->
      <b-card-body class="py-25">
        <!-- First Row -->
        <b-row class="align-items-center">
          <!-- Date Range Filter -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0"
          >
            <flat-picker
              v-model="dateRange"
              placeholder="Taken Date Range"
              :config="{ mode: 'range' }"
              @on-close="fetchLoans"
            />
          </b-col>

          <!-- Borrower Filter -->
          <b-col
            cols="12"
            md="3"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.borrower"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="borrowerOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Borrower"
              multiple
              :close-on-select="false"
              :deselect-from-dropdown="true"
              @input="fetchLoans"
            />
          </b-col>
          <!-- Total Display -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <div class="total-expense-display">
              <span class="total-label">Total:</span>
              <span class="total-amount">₹{{ totalAmount }}</span>
            </div>
          </b-col>

          <!-- Status Filter -->
          <b-col
            cols="12"
            md="1"
            class="mb-0 mb-md-0 px-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.status"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Status"
              :close-on-select="false"
              :deselect-from-dropdown="true"
              @input="fetchLoans"
            />
          </b-col>

          <!-- Min Amount Filter -->
          <b-col
            cols="12"
            md="1"
            style="padding-right: 1px;"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.min_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Min Amt"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Max Amount Filter -->
          <b-col
            cols="12"
            md="1"
            class="mb-0 mb-md-0 px-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.max_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Max Amt"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Toggle & Clear Buttons -->
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-center justify-content-md-end mt-25 mt-md-0"
          >
            <b-button
              v-b-tooltip.hover
              title="Generate Report"
              variant="outline-success"
              size="sm"
              class="mr-50"
              :disabled="fetchingReport"
              @click="fetchReportData"
            >
              <b-spinner
                v-if="fetchingReport"
                small
                label="Loading..."
              />
              <feather-icon
                v-else
                icon="FileTextIcon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Clear All Filters"
              variant="outline-secondary"
              size="sm"
              class="mr-50"
              @click="clearAllFilters"
            >
              Clear
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Toggle Additional Filters"
              variant="outline-primary"
              size="sm"
              @click="showAdditionalFilters = !showAdditionalFilters"
            >
              <feather-icon
                :icon="showAdditionalFilters ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="14"
              />
            </b-button>
          </b-col>
        </b-row>

        <!-- Second Row (Collapsible) -->
        <b-collapse
          v-model="showAdditionalFilters"
          class="mt-50"
        >
          <b-row class="align-items-center">
            <!-- Repayment Date Range Filter -->
            <b-col
              cols="12"
              md="2"
              class="mb-0 mb-md-0"
            >
              <flat-picker
                v-model="repaymentDateRange"
                placeholder="Repayment Date Range"
                :config="{ mode: 'range' }"
                @on-close="fetchLoans"
              />
            </b-col>

            <!-- Repayment Mode Filter -->
            <b-col
              cols="12"
              md="2"
              class="mb-0 mb-md-0 mt-25 mt-md-0"
            >
              <custom-v-select
                v-model="filters.repayment_mode"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="repaymentModeOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Repayment Mode"
                multiple
                :close-on-select="false"
                :deselect-from-dropdown="true"
                @input="fetchLoans"
              />
            </b-col>
            <!-- Created By Filter -->
            <b-col
              cols="12"
              md="2"
              class="mb-0 mb-md-0 mt-25 mt-md-0"
            >
              <custom-v-select
                v-model="filters.created_by"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="usersList"
                label="full_name"
                placeholder="Created By"
                multiple
                :close-on-select="false"
                :reduce="data => data.id"
                :deselect-from-dropdown="true"
                @input="fetchLoans"
              />
            </b-col>

            <!-- Repayment Received By Filter -->
            <b-col
              cols="12"
              md="3"
              class="mb-0 mb-md-0 mt-25 mt-md-0"
            >
              <custom-v-select
                v-model="filters.repayment_received_by"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="usersList"
                label="full_name"
                placeholder="Repayment Received By"
                multiple
                :close-on-select="false"
                :reduce="data => data.id"
                :deselect-from-dropdown="true"
                @input="fetchLoans"
              />
            </b-col>
          </b-row>
        </b-collapse>
      </b-card-body>

      <!-- Add Loan Form -->
      <b-card-body class="loan-form-wrapper">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="3"
          >
            <v-select
              id="borrower-select"
              ref="borrowerSelect"
              v-model="newLoan.borrower"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="borrowerOptions"
              :reduce="option => option.value"
              :disabled="isAdding"
              label="text"
              placeholder="Select Borrower"
              class="loan-select-sm"
              @input="focusAmount"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mt-25 mt-md-0"
          >
            <b-form-input
              id="amount-input"
              ref="amountInput"
              v-model="newLoan.amount"
              type="number"
              size="sm"
              placeholder="Enter Amount"
              :disabled="isAdding"
              :class="{ 'border-danger': newLoan.amount !== null && newLoan.amount <= 0 }"
              @keydown.enter="focusRemark"
            />
          </b-col>
          <b-col
            cols="12"
            md="5"
            class="mt-25 mt-md-0"
          >
            <b-form-input
              id="remark-input"
              ref="remarkInput"
              v-model="newLoan.remarks"
              size="sm"
              placeholder="Enter Remarks"
              :disabled="isAdding"
              @keydown.enter="handleRemarkEnter"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mt-25 mt-md-0"
          >
            <b-button
              ref="addButton"
              variant="primary"
              size="sm"
              block
              :disabled="!isValidNewLoan || isAdding"
              @click="addLoan"
            >
              <b-spinner
                v-if="isAdding"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="PlusIcon"
                size="14"
                class="mr-25"
              />
              {{ isAdding ? 'Adding...' : 'Add' }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Loans Table -->
      <div class="table-responsive">
        <table class="table table-bordered table-sm mb-0">
          <thead>
            <tr class="bg-light">
              <th style="width: 2%;">
                #
              </th>
              <th style="width: 17%;">
                Borrower
              </th>
              <th
                style="width: 7%;"
                class="text-right"
              >
                Amount (₹)
                <div class="font-weight-bold text-danger">
                  {{ formatAmount( totalLoanAmount) }}
                </div>
              </th>
              <th style="width: 5%;">
                Status
              </th>
              <th style="width: 12%;">
                Remarks
              </th>
              <th style="width: 10%;">
                Repayment Mode
              </th>
              <th style="width: 10%;">
                Repayment Remarks
              </th>
              <th style="width: 12%;">
                Taken At
              </th>
              <th style="width: 12%;">
                Repayed At
              </th>
              <th style="width: 3%;">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty State -->
            <tr v-if="loans.length === 0 && !loading">
              <td
                colspan="10"
                class="text-center text-muted py-2"
              >
                No loans found
              </td>
            </tr>

            <!-- Loading State -->
            <tr v-if="loading">
              <td
                colspan="10"
                class="text-center py-2"
              >
                <b-spinner
                  small
                  class="mr-50"
                />
                Loading loans...
              </td>
            </tr>
            <!-- Existing Loans -->
            <tr
              v-for="(loan, index) in loans"
              :key="loan.id"
              :class="getRowClass(loan)"
            >
              <template v-if="editingId === loan.id">
                <!-- Edit Mode -->
                <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                <td>
                  <select
                    v-model="editingLoan.borrower"
                    class="form-control form-control-sm"
                    :disabled="isUpdating"
                  >
                    <option
                      v-for="option in borrowerOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="editingLoan.amount"
                    type="number"
                    size="sm"
                    class="text-right"
                    :disabled="isUpdating"
                    :class="{ 'border-danger': editingLoan.amount !== null && editingLoan.amount <= 0 }"
                  />
                </td>
                <td>
                  <b-badge
                    :variant="editingLoan.status === 'open' ? 'warning' : 'success'"
                    class="text-capitalize"
                  >
                    {{ editingLoan.status }}
                  </b-badge>
                </td>
                <td>
                  <b-form-input
                    v-model="editingLoan.remarks"
                    size="sm"
                    :disabled="isUpdating"
                  />
                </td>
                <td>
                  <select
                    v-model="editingLoan.repayment_mode"
                    class="form-control form-control-sm"
                    :disabled="isUpdating"
                    @change="handleRepaymentModeChange"
                  >
                    <option :value="null">
                      Payment Mode
                    </option>
                    <option
                      v-for="option in repaymentModeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </td>
                <td>
                  <b-form-input
                    v-model="editingLoan.repayment_remarks"
                    size="sm"
                    placeholder="Repayment remarks"
                    :disabled="isUpdating"
                  />
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ loan.taken_at ? formatedDate(loan.taken_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ loan.created_by_name || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ loan.repayed_at ? formatedDate(loan.repayed_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ loan.repayment_received_by_name || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <b-spinner
                    v-if="isUpdating"
                    small
                    variant="success"
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 cursor-pointer text-success"
                    :class="{ 'text-muted': !isValidEdit }"
                    @click="isValidEdit && updateLoan()"
                  />
                  <feather-icon
                    icon="XIcon"
                    size="18"
                    class="cursor-pointer"
                    :class="{ 'text-muted': isUpdating }"
                    @click="!isUpdating && cancelEdit()"
                  />
                </td>
              </template>
              <template v-else>
                <!-- View Mode -->
                <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                <td>
                  <span v-if="loan.borrower_contact_no">
                    {{ loan.borrower_name }} - {{ loan.borrower_contact_no }}
                  </span>
                  <span v-else>
                    {{ loan.borrower_name }}
                  </span>
                </td>
                <td class="text-right">
                  {{ formatAmount(loan.amount) }}
                </td>
                <td>
                  <b-badge
                    :variant="loan.status === 'open' ? 'warning' : 'success'"
                    class="text-capitalize"
                  >
                    {{ loan.status }}
                  </b-badge>
                </td>
                <td>
                  <feather-icon
                    v-if="loan.remarks"
                    v-b-tooltip.hover="{boundary:'window'}"
                    :title="loan.remarks"
                    icon="MessageSquareIcon"
                    size="20"
                    class="cursor-pointer"
                  />
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </td>
                <td>
                  <span v-if="loan.repayment_mode">
                    {{ getRepaymentModeName(loan.repayment_mode) }}
                  </span>
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </td>
                <td>
                  <feather-icon
                    v-if="loan.repayment_remarks"
                    v-b-tooltip.hover="{boundary:'window'}"
                    :title="loan.repayment_remarks"
                    icon="MessageSquareIcon"
                    size="20"
                    class="cursor-pointer"
                  />
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ loan.taken_at ? formatedDate(loan.taken_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ loan.created_by_name || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ loan.repayed_at ? formatedDate(loan.repayed_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ loan.repayment_received_by_name || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <a
                    href="javascript:void(0)"
                    class="ml-2 text-info"
                    :class="{ 'disabled-link': deletingId }"
                    @click="deletingId ? null : startEdit(loan)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="16"
                    />
                  </a>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <detailed-pagination
        v-if="!loading && !loadingError"
        :total-items="totalItems"
        :per-page="perPage"
        :current-page="currentPage"
        @page-changed="(page) => {currentPage = page}"
      />
    </b-card>
    <loans-report-modal
      v-model="showReportModal"
      :loans-data="reportData"
      :date-range="dateRange"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
  BCollapse,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import LoansReportModal from './LoansReportModal.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    BCollapse,
    FlatPicker,
    vSelect,
    CustomVSelect,
    DetailedPagination,
    LoansReportModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      // Date Range - Set today's date by default
      dateRange: null,
      repaymentDateRange: null,

      // UI State
      showAdditionalFilters: false,

      // Filters
      filters: {
        borrower: [],
        status: [],
        created_by: [],
        repayment_mode: [],
        repayment_received_by: [],
        min_amount: null,
        max_amount: null,
      },

      // Data
      loans: [],
      borrowerOptions: [],
      statusOptions: [
        { value: 'open', text: 'Open' },
        { value: 'closed', text: 'Closed' },
      ],
      repaymentModeOptions: [
        { value: 'cash', text: 'Cash' },
        { value: 'upi', text: 'UPI' },
        { value: 'cheque', text: 'Cheque' },
        { value: 'rgts', text: 'RGTS' },
        { value: 'credit_note', text: 'Credit Note' },
        { value: 'transferred_to_salary_advance', text: 'Transferred to Salary Adv' },
        { value: 'transferred_to_medicine_advance', text: 'Transferred to Medicine Adv' },
      ],
      usersList: [],

      // Loading States
      loading: false,
      isAdding: false,
      isUpdating: false,
      deletingId: null,
      fetchingReport: false,

      // Error State
      loadingError: null,

      // Add New Loan
      newLoan: {
        borrower: null,
        amount: null,
        remarks: '',
        status: 'open',
      },

      // Edit Loan
      editingId: null,
      editingLoan: {
        borrower: null,
        amount: null,
        remarks: '',
        status: 'open',
        repayment_mode: null,
        repayment_remarks: '',
      },

      // Original status to track changes
      originalStatus: null,

      // Pagination
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      totalAmount: 0,

      // Debounce timer
      debounceTimer: null,

      // Report Modal
      showReportModal: false,
      reportData: [],

    }
  },
  computed: {
    isValidNewLoan() {
      return (
        this.newLoan.borrower
        && this.newLoan.amount
        && this.newLoan.amount > 0
      )
    },
    isValidEdit() {
      const basicValid = (
        this.editingLoan.borrower
        && this.editingLoan.amount
        && this.editingLoan.amount > 0
      )

      return basicValid
    },
    totalLoanAmount() {
      return this.loans.reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0)
    },
  },
  watch: {
    currentPage() {
      this.fetchLoans()
    },
    dateRange() {
      this.currentPage = 1
      this.fetchLoans()
    },
    repaymentDateRange() {
      this.currentPage = 1
      this.fetchLoans()
    },
  },
  mounted() {
    this.fetchBorrowerOptions()
    this.fetchUsersList()
    this.fetchLoans()
    // Focus on borrower select when component loads
    this.$nextTick(() => {
      this.focusBorrowerSelect()
    })
  },
  activated() {
    // Focus on borrower select when component is activated (for keep-alive components in tabs)
    this.$nextTick(() => {
      this.focusBorrowerSelect()
    })
  },
  methods: {
    fetchData() {
      this.fetchLoans()
    },
    // Debounced filter change
    debouncedFiltersChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchLoans()
      }, 500)
    },

    // Focus Management Methods
    focusBorrowerSelect() {
      if (this.$refs.borrowerSelect) {
        const selectElement = this.$refs.borrowerSelect.$el.querySelector('input')
        if (selectElement) {
          selectElement.focus()
        }
      }
    },

    focusAmount() {
      this.$nextTick(() => {
        if (this.$refs.amountInput) {
          this.$refs.amountInput.focus()
        }
      })
    },

    focusRemark() {
      this.$nextTick(() => {
        if (this.$refs.remarkInput) {
          this.$refs.remarkInput.focus()
        }
      })
    },

    handleRemarkEnter() {
      // If form is valid, submit; otherwise focus on the first invalid field
      if (this.isValidNewLoan && !this.isAdding) {
        this.addLoan()
      } else if (!this.newLoan.borrower) {
        this.focusBorrowerSelect()
      } else if (!this.newLoan.amount || this.newLoan.amount <= 0) {
        this.focusAmount()
      }
    },

    // Handle repayment mode change
    handleRepaymentModeChange() {
      // Automatically set status to 'closed' when repayment_mode is selected
      if (this.editingLoan.repayment_mode && this.editingLoan.status === 'open') {
        this.editingLoan.status = 'closed'
      }
    },

    // API Calls
    async fetchBorrowerOptions() {
      try {
        const response = await axios.get('/operations/borrowers?per_page=500')
        this.borrowerOptions = response.data && response.data.results.map(borrower => ({
          value: borrower.id,
          text: borrower.contact_no ? `${borrower.name} - ${borrower.contact_no}` : borrower.name,
        }))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load borrowers'
      }
    },

    async fetchUsersList() {
      try {
        const { data: users } = await axios.get('/delivery/users_list_by_group?group=operations_cashreg')
        this.usersList = users
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to fetch users. Please try again.'
      }
    },

    async fetchLoans() {
      this.loading = true
      this.loadingError = null
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        }

        if (this.dateRange) {
          const [startDate, endDate] = this.dateRange.split(' to ')
          if (startDate && endDate) {
            params.start_date = startDate
            params.end_date = endDate
          }
        }

        if (this.repaymentDateRange) {
          const [repaymentStartDate, repaymentEndDate] = this.repaymentDateRange.split(' to ')
          if (repaymentStartDate && repaymentEndDate) {
            params.repayment_start_date = repaymentStartDate
            params.repayment_end_date = repaymentEndDate
          }
        }

        // Add filters
        if (this.filters.borrower && this.filters.borrower.length > 0) {
          params.borrower = this.filters.borrower
        }
        if (this.filters.status && this.filters.status.length > 0) {
          params.status = this.filters.status
        }
        if (this.filters.created_by && this.filters.created_by.length > 0) {
          params.created_by = this.filters.created_by
        }
        if (this.filters.repayment_mode && this.filters.repayment_mode.length > 0) {
          params.repayment_mode = this.filters.repayment_mode
        }
        if (this.filters.repayment_received_by && this.filters.repayment_received_by.length > 0) {
          params.repayment_received_by = this.filters.repayment_received_by
        }
        if (this.filters.min_amount !== null && this.filters.min_amount !== '') {
          params.min_amount = this.filters.min_amount
        }
        if (this.filters.max_amount !== null && this.filters.max_amount !== '') {
          params.max_amount = this.filters.max_amount
        }

        const response = await axios.get(`/operations/loans/?${getParsedQueryString(params)}`)
        this.loans = response.data.results
        this.totalItems = response.data.count
        this.totalAmount = parseFloat(response.data.total_amount.toFixed(2))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load loans. Please try again.'
        this.loans = []
        this.totalItems = 0
      } finally {
        this.loading = false
      }
    },
    // Fetch Report Data
    async fetchReportData() {
      this.fetchingReport = true
      this.loadingError = null
      try {
        const params = {
          page: 1,
          per_page: 500,
          sort_by: 'borrower__name',
        }

        if (this.dateRange) {
          const [startDate, endDate] = this.dateRange.split(' to ')
          if (startDate && endDate) {
            params.start_date = startDate
            params.end_date = endDate
          }
        }

        if (this.repaymentDateRange) {
          const [repaymentStartDate, repaymentEndDate] = this.repaymentDateRange.split(' to ')
          if (repaymentStartDate && repaymentEndDate) {
            params.repayment_start_date = repaymentStartDate
            params.repayment_end_date = repaymentEndDate
          }
        }

        // Add filters
        if (this.filters.borrower && this.filters.borrower.length > 0) {
          params.borrower = this.filters.borrower
        }
        if (this.filters.status && this.filters.status.length > 0) {
          params.status = this.filters.status
        }
        if (this.filters.created_by && this.filters.created_by.length > 0) {
          params.created_by = this.filters.created_by
        }
        if (this.filters.repayment_mode && this.filters.repayment_mode.length > 0) {
          params.repayment_mode = this.filters.repayment_mode
        }
        if (this.filters.repayment_received_by && this.filters.repayment_received_by.length > 0) {
          params.repayment_received_by = this.filters.repayment_received_by
        }
        if (this.filters.min_amount !== null && this.filters.min_amount !== '') {
          params.min_amount = this.filters.min_amount
        }
        if (this.filters.max_amount !== null && this.filters.max_amount !== '') {
          params.max_amount = this.filters.max_amount
        }

        const response = await axios.get(`/operations/loans/?${getParsedQueryString(params)}`)
        this.reportData = response.data.results
        this.showReportModal = true
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || 'Failed to fetch report data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.fetchingReport = false
      }
    },
    async addLoan() {
      if (!this.isValidNewLoan || this.isAdding) return

      this.isAdding = true
      this.loadingError = null
      try {
        const formData = {
          borrower: this.newLoan.borrower,
          amount: this.newLoan.amount,
          remarks: this.newLoan.remarks,
          status: 'open', // Always send 'open' for new loans
        }

        await axios.post('/operations/loans/', formData)

        await this.fetchLoans()
        this.resetNewLoan()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Loan created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')

        // Focus back to borrower select after successful submission
        this.$nextTick(() => {
          this.focusBorrowerSelect()
        })
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to add loan. Please try again.'
      } finally {
        this.isAdding = false
      }
    },

    async updateLoan() {
      if (!this.isValidEdit || this.isUpdating) return

      this.isUpdating = true
      this.loadingError = null
      try {
        const formData = {
          borrower: this.editingLoan.borrower,
          amount: this.editingLoan.amount,
          remarks: this.editingLoan.remarks,
          status: this.editingLoan.status,
        }

        // Only include repayment fields if status is closed
        if (this.editingLoan.status === 'closed') {
          formData.repayment_mode = this.editingLoan.repayment_mode
          if (this.editingLoan.repayment_remarks) {
            formData.repayment_remarks = this.editingLoan.repayment_remarks
          }
        }

        await axios.patch(`/operations/loans/${this.editingId}/`, formData)

        await this.fetchLoans()
        this.cancelEdit()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Loan updated successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to update loan. Please try again.'
      } finally {
        this.isUpdating = false
      }
    },

    async deleteLoan(id) {
      this.deletingId = id
      this.loadingError = null
      try {
        await axios.delete(`/operations/loans/${id}/`)

        await this.fetchLoans()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Loan deleted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to delete loan. Please try again.'
      } finally {
        this.deletingId = null
      }
    },

    // Edit Operations
    startEdit(loan) {
      this.editingId = loan.id
      this.originalStatus = loan.status
      this.editingLoan = {
        borrower: loan.borrower,
        amount: loan.amount,
        remarks: loan.remarks,
        status: loan.status,
        repayment_mode: loan.repayment_mode,
        repayment_remarks: loan.repayment_remarks || '',
      }
    },

    cancelEdit() {
      this.editingId = null
      this.originalStatus = null
      this.editingLoan = {
        borrower: null,
        amount: null,
        remarks: '',
        status: 'open',
        repayment_mode: null,
        repayment_remarks: '',
      }
    },

    resetNewLoan() {
      this.newLoan = {
        borrower: null,
        amount: null,
        remarks: '',
        status: 'open',
      }
    },

    // Clear All Filters
    clearAllFilters() {
      this.dateRange = null
      this.repaymentDateRange = null
      this.filters = {
        borrower: [],
        status: [],
        created_by: [],
        repayment_mode: [],
        repayment_received_by: [],
        min_amount: null,
        max_amount: null,
      }
      this.currentPage = 1
      this.fetchLoans()
    },

    // Helpers
    getBorrowerName(borrowerId) {
      const borrower = this.borrowerOptions.find(b => b.value === borrowerId)
      return borrower ? borrower.text : ''
    },

    getRepaymentModeName(mode) {
      const repaymentMode = this.repaymentModeOptions.find(m => m.value === mode)
      return repaymentMode ? repaymentMode.text : mode
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    getRowClass(loan) {
      // Customize row colors based on status
      if (loan.status === 'open') {
        return 'table-warning'
      }
      if (loan.status === 'closed') {
        return 'table-success'
      }
      return ''
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
  .total-expense-display {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 67, 51, 0.728);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(234, 84, 85, 0.4);
  height: 100%;
}

.total-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  margin-right: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
/* Minimal vertical spacing utilities */
.py-25 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-50 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.pb-50 {
  padding-bottom: 0.5rem !important;
}

.mb-25 {
  margin-bottom: 0.25rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mt-25 {
  margin-top: 0.25rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

/* Additional filters section styling */
.collapse:not(.show) {
  display: none;
}

.collapse.show {
  display: block;
}

/* Button group styling */
.d-flex > .btn + .btn {
  margin-left: 0;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.mr-75 {
  margin-right: 0.75rem !important;
}

/* Form wrapper with distinct background color */
.loan-form-wrapper {
  border: 2px solid #7367f0;
  border-radius: 0.5rem;
  margin: 0.5rem;
  padding: 0.5rem !important;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
}

.spinner {
  animation: spin 1s linear infinite;
}

/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Compact table styling */
.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered td,
.table-bordered th {
  border: 1px solid #dee2e6;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* v-select compact styling */
.loan-select-sm >>> .vs__dropdown-toggle {
  padding: 0;
  min-height: calc(1.5em + 0.5rem + 2px);
  border: 1px solid #d8d6de;
  background-color: #ffffff !important;
}

.loan-select-sm >>> .vs__search {
  padding: 0.25rem 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  background-color: transparent;
}

.loan-select-sm >>> .vs__selected {
  margin: 0.125rem 0.25rem;
  padding: 0.125rem 0.25rem;
  font-size: 0.875rem;
  background-color: #f3f2f7;
}

.loan-select-sm >>> .vs__actions {
  padding: 0 0.25rem;
}

.loan-select-sm >>> .vs__dropdown-menu {
  background-color: #ffffff;
}

.loan-select-sm >>> .vs__clear {
  margin-right: 0;
}

/* Badge styling */
.badge {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
}

/* Icon links styling */
a.text-info:hover {
  color: #0056b3 !important;
  text-decoration: none;
}

a.text-danger:hover {
  color: #c82333 !important;
  text-decoration: none;
}

/* Disabled link styling */
.disabled-link {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive table */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Table row color classes */
.table-warning {
  background-color: #fff3cd !important;
}

.table-success {
  background-color: #d4edda !important;
}

/* Cursor pointer for clickable icons */
.cursor-pointer {
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .loan-form-wrapper {
    margin: 0.25rem;
    padding: 0.5rem !important;
  }

  .justify-content-md-end {
    justify-content: flex-start !important;
  }
}
</style>
